import React, { useEffect, useReducer, useState } from "react";
import DashboardContainer from "../../components/dashboard-container";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { arciveOrderService } from "../../utils/services/archive-order.service";
import { useNavigate } from "react-router-dom";
import HistoricalOrderList from "../../components/historical-order-list";
import ManageArchiveOrder from "../../components/manage-archive-order";
import dayjs from "dayjs";
import { isMobile } from "../../globalStyles/mobile";
import ImportIcon from "../../globalStyles/icons/importIcon";
import { historicalOrderFilterInitialState } from "../../utils/filter-config";
import { buildQueryObject } from "../../utils/query-builder";

const initialData = {
  order_id: "",
  client: null,
  order_date: "",
  category: "",
  item: "",
  currency: "",
  price: "",
  sku: "",
  size: "",
  qty: "",
};

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state.columns.map((x) =>
          x.key === action.payload.key ? { ...x, ...action.payload } : x,
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };

    default:
      return state;
  }
}

const ArchiveOrder = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [archiveOrders, setArchiveOrders] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [dataCount, setDataCount] = useState(rowsPerPage);
  const [loading, setLoading] = useState(false);
  const clientId = localStorage.getItem("client_id");
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialData);
  const [formErrors, setFormErrors] = useState({});
  const roles = localStorage.getItem("roles")
    ? JSON.parse(localStorage.getItem("roles"))
    : [];
  const theme = useTheme();
  const text_color = theme?.palette?.text?.primary || "#FFF";
  const [filterData, dispatch] = useReducer(
    filterReducer,
    historicalOrderFilterInitialState,
  );

  const fetchHistoricalOrders = async () => {
    setLoading(true);
    try {
      const queryObject = buildQueryObject(filterData, page);

      // Add client_id to query if available
      if (clientId) {
        queryObject.client_id = clientId;
      }

      const response = await arciveOrderService.filter(queryObject);
      setArchiveOrders(response.data);
      setLoading(false);
      setDataCount(response.total);
    } catch (error) {
      console.error("Fetch failed:", error);
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchHistoricalOrders();
  }, [page, rowsPerPage, filterData]);

  const handleInputChange = (col, e) => {
    setFormData((prevData) => ({
      ...prevData,
      [col]: e,
    }));
  };

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };
  const handleEdit = (order) => {
    setSelectedOrder(order);
    setFormData({
      ...order,
      client: order.client || null,
      order_date: order?.order_date ? dayjs(order.order_date) : null,
    });
    setShowAddModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.order_id) {
      errors.order_id = "Order ID is required";
    }
    if (!formData.client && !clientId) {
      errors.businessName = "Select the Client";
    }
    if (!formData.order_date) {
      errors.order_date = "Order date is required";
    }
    if (!formData.item) {
      errors.item = "Provide a valid item.";
    }
    if (!formData.category) {
      errors.category = "Provide a valid category.";
    }
    if (!formData.sku) {
      errors.sku = "Provide a valid SKU.";
    }
    if (!formData.currency) {
      errors.currency = "Select the Currency.";
    }
    if (!formData.price) {
      errors.price = "Enter the price.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createArchiveOrder = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      if (formData._id) {
        let obj = JSON.parse(JSON.stringify(formData));
        delete obj._id;
        let resp = await arciveOrderService.edit(formData._id, obj);
        // setArchiveOrders(
        //   archiveOrders.map((x) => (x._id === formData._id ? resp : x))
        // );
      } else {
        let resp = await arciveOrderService.create(formData);
        //setArchiveOrders([resp, ...archiveOrders]);
      }
      await fetchHistoricalOrders();
      onCloseModal();
    } catch (error) {
      console.error("Historical Order create/edit failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const onCloseModal = () => {
    setShowAddModal(false);
    setFormData(initialData);
  };

  return (
    <>
      <DashboardContainer
        filterButton={false}
        hideUploadButton
        hideAddButton
        header={"Historical Orders"}
        setViewMode={setViewMode}
        hideFilterClient={true}
        onClickRefresh={() => fetchHistoricalOrders()}
        headerButtons={
          <>
            {roles.find(
              (x) =>
                x.name === "Client - Premium" || x.name === "Client - Standard",
            ) ? (
              isMobile() ? null : (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={1}
                  justifyContent="flex-start"
                >
                  <Button
                    size="small"
                    style={{
                      textTransform: "none",
                      marginRight: 10,
                    }}
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigate("/historical-data-import");
                    }}
                  >
                    <ImportIcon fill={"#ffff"} />
                    <Typography
                      fontSize="14px"
                      fontWeight={500}
                      sx={{
                        color: text_color,
                        marginLeft: 1,
                      }}
                      fontFamily={"poppins"}
                    >
                      Import
                    </Typography>
                  </Button>
                </Box>
              )
            ) : null}
          </>
        }
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9999,
              color: "white",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <HistoricalOrderList
            dataCount={dataCount}
            page={page}
            rowsPerPage={filterData.limit}
            setRowsPerPage={(e) =>
              dispatch({
                type: "UPDATE_LIMIT",
                payload: e,
              })
            }
            onChangeFilter={handleFilterChange}
            filterData={filterData}
            setPage={setPage}
            onDeleteData={fetchHistoricalOrders}
            onEditData={(order) => handleEdit(order)}
            data={archiveOrders}
            viewMode={viewMode}
            selectedOrder={selectedOrder}
            setSelectedOrder={setSelectedOrder}
          />
        )}
        <ManageArchiveOrder
          formData={formData}
          onChangeData={(col, e) => handleInputChange(col, e)}
          title={"Client"}
          onClickAdd={createArchiveOrder}
          handleClose={() => onCloseModal()}
          open={showAddModal}
          formErrors={formErrors}
          client={clientId}
        ></ManageArchiveOrder>
      </DashboardContainer>
    </>
  );
};

export default ArchiveOrder;
